import { useEffect, useMemo, useRef, useState } from "react";

import { enrichOrderRoutes } from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import { PageActions } from "../components/PageActions.jsx";
import { CustomerShippingAddressesTab } from "../components/master-data/CustomerShippingAddressesTab.jsx";
import { CustomersTab } from "../components/master-data/CustomersTab.jsx";
import { DepotsTab } from "../components/master-data/DepotsTab.jsx";
import { DriverAccessTab } from "../components/master-data/DriverAccessTab.jsx";
import {
  AreaOverridesTab,
  RouteMappingsTab,
  RoutesTab,
} from "../components/master-data/EditableMasterTabs.jsx";
import {
  CoDriversTab,
  DriversTab,
  FoRoutesTab,
  VehiclesTab,
} from "../components/master-data/FleetMasterTabs.jsx";
import { PoisTab } from "../components/master-data/PoisTab.jsx";
import {
  SkuLogisticsTab,
  SkuUomMappingsTab,
} from "../components/master-data/SkuLogisticsTabs.jsx";
import { StockTab } from "../components/master-data/StockTab.jsx";

const TAB_GROUPS = [
  {
    id: "accurate",
    label: "Data Accurate",
    description: "Pelanggan, stok, item, dan struktur satuan dari Accurate.",
    icon: "database",
    tabs: [
      { id: "customers", label: "Pelanggan" },
      { id: "stock", label: "Stok" },
      { id: "sku-logistics", label: "SKU Logistics" },
      { id: "uom-mappings", label: "Struktur UOM" },
    ],
  },
  {
    id: "location-route",
    label: "Lokasi & Rute",
    description: "Alamat, titik operasional, area, dan rute pengiriman.",
    icon: "map",
    tabs: [
      { id: "shipping-addresses", label: "Alamat Kirim" },
      { id: "depots", label: "Depot" },
      { id: "pois", label: "POI" },
      { id: "routes", label: "Rute" },
      { id: "mappings", label: "Mapping Area" },
      { id: "overrides", label: "Normalisasi Area" },
      { id: "fo-routes", label: "Rute FO" },
    ],
  },
  {
    id: "people-fleet",
    label: "SDM & Armada",
    description: "Driver, akses Sedulur Dalan, co-driver, dan kendaraan.",
    icon: "truck",
    tabs: [
      { id: "drivers", label: "Driver" },
      { id: "driver-access", label: "Akses Sedulur Dalan" },
      { id: "co-drivers", label: "Co-Driver" },
      { id: "vehicles", label: "Kendaraan" },
    ],
  },
];

const TABS = TAB_GROUPS.flatMap((group) =>
  group.tabs.map((tab) => ({ ...tab, groupId: group.id })),
);
const TAB_BY_ID = new Map(TABS.map((tab) => [tab.id, tab]));
const GROUP_BY_ID = new Map(TAB_GROUPS.map((group) => [group.id, group]));

const ENRICHMENT_TABS = new Set([
  "customers",
  "shipping-addresses",
  "routes",
  "mappings",
  "overrides",
]);


function getKeyboardTargetIndex(event, currentIndex, itemCount) {
  if (!itemCount) return null;

  if (event.key === "ArrowRight" || event.key === "ArrowDown") {
    return (currentIndex + 1) % itemCount;
  }
  if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
    return (currentIndex - 1 + itemCount) % itemCount;
  }
  if (event.key === "Home") return 0;
  if (event.key === "End") return itemCount - 1;
  return null;
}

function getInitialDataMasterState() {
  const params = new URLSearchParams(window.location.search);
  const requestedTab = params.get("tab");
  const activeTab = TAB_BY_ID.has(requestedTab) ? requestedTab : "customers";

  return {
    activeTab,
    customerSearch: params.get("search")?.trim() || "",
  };
}

export function DataMasterPage() {
  const [initialState] = useState(getInitialDataMasterState);
  const [activeTab, setActiveTab] = useState(initialState.activeTab);
  const [enriching, setEnriching] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const groupRefs = useRef(new Map());
  const tabRefs = useRef(new Map());

  const activeGroup = useMemo(
    () => GROUP_BY_ID.get(TAB_BY_ID.get(activeTab)?.groupId) || TAB_GROUPS[0],
    [activeTab],
  );
  const activeTabLabel = TAB_BY_ID.get(activeTab)?.label || "Pelanggan";

  useEffect(() => {
    tabRefs.current.get(activeTab)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [activeTab]);

  function selectTab(tabId) {
    setActiveTab(tabId);

    const params = new URLSearchParams(window.location.search);
    params.set("tab", tabId);
    if (tabId !== "customers") params.delete("search");

    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );
  }

  function handleGroupKeyDown(event, currentIndex) {
    const nextIndex = getKeyboardTargetIndex(
      event,
      currentIndex,
      TAB_GROUPS.length,
    );
    if (nextIndex === null) return;

    event.preventDefault();
    const nextGroup = TAB_GROUPS[nextIndex];
    selectTab(nextGroup.tabs[0].id);
    window.requestAnimationFrame(() => {
      groupRefs.current.get(nextGroup.id)?.focus();
    });
  }

  function handleModuleKeyDown(event, currentIndex) {
    const nextIndex = getKeyboardTargetIndex(
      event,
      currentIndex,
      activeGroup.tabs.length,
    );
    if (nextIndex === null) return;

    event.preventDefault();
    const nextTab = activeGroup.tabs[nextIndex];
    selectTab(nextTab.id);
    window.requestAnimationFrame(() => {
      tabRefs.current.get(nextTab.id)?.focus();
    });
  }

  async function runEnrichment() {
    setEnriching(true);
    setMessage("");
    setError("");

    try {
      const payload = await enrichOrderRoutes();
      setMessage(payload.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setEnriching(false);
    }
  }

  function renderActiveTab() {
    if (activeTab === "customers") {
      return <CustomersTab initialSearch={initialState.customerSearch} />;
    }
    if (activeTab === "shipping-addresses") return <CustomerShippingAddressesTab />;
    if (activeTab === "depots") return <DepotsTab />;
    if (activeTab === "pois") return <PoisTab />;
    if (activeTab === "stock") return <StockTab />;
    if (activeTab === "sku-logistics") return <SkuLogisticsTab />;
    if (activeTab === "uom-mappings") return <SkuUomMappingsTab />;
    if (activeTab === "routes") return <RoutesTab />;
    if (activeTab === "mappings") return <RouteMappingsTab />;
    if (activeTab === "overrides") return <AreaOverridesTab />;
    if (activeTab === "drivers") return <DriversTab />;
    if (activeTab === "driver-access") return <DriverAccessTab />;
    if (activeTab === "co-drivers") return <CoDriversTab />;
    if (activeTab === "vehicles") return <VehiclesTab />;
    if (activeTab === "fo-routes") return <FoRoutesTab />;
    return null;
  }

  return (
    <main className="master-page dm-page dm-control-page">
      <header className="master-page__header dm-command-header">
        <div className="dm-command-header__identity">
          <span className="dm-command-header__eyebrow">Pusat Data Operasional</span>
          <div className="dm-command-header__title-row">
            <h1>Data Master</h1>
            <span className="dm-command-header__context">{activeTabLabel}</span>
          </div>
          <p>
            Kelola sumber data Accurate, lokasi, rute, SDM, dan armada dalam
            satu workspace yang konsisten.
          </p>
        </div>

        <div className="dm-command-header__system" aria-label="Konteks data master">
          <span aria-hidden="true" className="dm-command-header__system-icon">
            <Icon name="database" size={18} />
          </span>
          <div className="dm-command-header__system-block">
            <span>Sumber data</span>
            <strong>Accurate + Manual</strong>
            <small>Sinkronisasi dan pengelolaan internal</small>
          </div>
          <div className="dm-command-header__system-block dm-command-header__system-block--context">
            <span>Ruang lingkup aktif</span>
            <strong>{activeGroup.label}</strong>
            <small>{activeGroup.tabs.length} modul</small>
          </div>
        </div>

        {ENRICHMENT_TABS.has(activeTab) && (
          <div className="dm-command-header__actions">
            <PageActions>
              <button
                className="primary-button dm-command-header__primary-action"
                disabled={enriching}
                onClick={runEnrichment}
                type="button"
              >
                <span className={enriching ? "is-spinning" : ""}>
                  <Icon name="sync" size={15} />
                </span>
                {enriching ? "Memperbarui..." : "Perbarui Area & Rute"}
              </button>
            </PageActions>
          </div>
        )}
      </header>

      {message && <div aria-live="polite" className="notice-banner dm-page-banner" role="status">{message}</div>}
      {error && <div className="error-banner dm-page-banner" role="alert">{error}</div>}

      <div className="dm-workspace-navigation">
        <div className="dm-mobile-scope-selector">
          <label htmlFor="dm-mobile-scope">Ruang lingkup</label>
          <div className="dm-mobile-scope-selector__control">
            <span aria-hidden="true">
              <Icon name={activeGroup.icon} size={16} />
            </span>
            <select
              id="dm-mobile-scope"
              onChange={(event) => {
                const group = GROUP_BY_ID.get(event.target.value);
                if (group) selectTab(group.tabs[0].id);
              }}
              value={activeGroup.id}
            >
              {TAB_GROUPS.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.label} · {group.tabs.length} modul
                </option>
              ))}
            </select>
          </div>
        </div>

        <nav
          aria-label="Kelompok data master"
          aria-orientation="horizontal"
          className="dm-group-tabs"
          role="tablist"
        >
          {TAB_GROUPS.map((group, index) => {
            const active = group.id === activeGroup.id;
            return (
              <button
                aria-controls="dm-module-navigation"
                aria-selected={active}
                className={active ? "is-active" : ""}
                id={`dm-group-tab-${group.id}`}
                key={group.id}
                onClick={() => selectTab(group.tabs[0].id)}
                onKeyDown={(event) => handleGroupKeyDown(event, index)}
                ref={(node) => {
                  if (node) groupRefs.current.set(group.id, node);
                  else groupRefs.current.delete(group.id);
                }}
                role="tab"
                tabIndex={active ? 0 : -1}
                type="button"
              >
                <span className="dm-group-tabs__icon">
                  <Icon name={group.icon} size={16} />
                </span>
                <span className="dm-group-tabs__copy">
                  <strong>{group.label}</strong>
                  <small>{group.description}</small>
                </span>
                <span className="dm-group-tabs__count">{group.tabs.length}</span>
              </button>
            );
          })}
        </nav>

        <div className="dm-module-navigation" id="dm-module-navigation">
          <div className="dm-module-navigation__context">
            <span className="dm-module-navigation__icon">
              <Icon name={activeGroup.icon} size={15} />
            </span>
            <strong>{activeGroup.label}</strong>
          </div>

          <nav
            aria-label="Modul data master"
            aria-orientation="horizontal"
            className="dm-module-tabs"
            role="tablist"
          >
            {activeGroup.tabs.map((tab, index) => {
              const active = tab.id === activeTab;
              return (
                <button
                  aria-controls="dm-workspace-panel"
                  aria-selected={active}
                  className={active ? "is-active" : ""}
                  id={`dm-module-tab-${tab.id}`}
                  key={tab.id}
                  onClick={() => selectTab(tab.id)}
                  onKeyDown={(event) => handleModuleKeyDown(event, index)}
                  ref={(node) => {
                    if (node) tabRefs.current.set(tab.id, node);
                    else tabRefs.current.delete(tab.id);
                  }}
                  role="tab"
                  tabIndex={active ? 0 : -1}
                  type="button"
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div
        aria-labelledby={`dm-module-tab-${activeTab}`}
        className="dm-workspace-body"
        id="dm-workspace-panel"
        role="tabpanel"
        tabIndex={-1}
      >
        {renderActiveTab()}
      </div>
    </main>
  );
}
